import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { CreatePlanningCyclePayload } from '../types/consensus.types';

function toDateInputValue(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function defaultCycleDates() {
  const today = new Date();
  const periodStart = new Date(today.getFullYear(), today.getMonth(), 1);
  const periodEnd = new Date(today.getFullYear(), today.getMonth() + 4, 0);
  return {
    period_start: toDateInputValue(periodStart),
    period_end: toDateInputValue(periodEnd),
  };
}

interface Props {
  saving?: boolean;
  onSubmit: (payload: CreatePlanningCyclePayload) => Promise<void>;
  onCancel: () => void;
}

export function CreatePlanningCycleForm({ saving, onSubmit, onCancel }: Props) {
  const { t } = useTranslation();
  const defaults = defaultCycleDates();
  const [form, setForm] = useState({
    name: '',
    cycle_type: 'monthly',
    period_start: defaults.period_start,
    period_end: defaults.period_end,
    version_name: 'Baseline',
  });

  const submit = async () => {
    if (!form.name.trim()) return;
    await onSubmit({
      name: form.name.trim(),
      cycle_type: form.cycle_type,
      period_start: form.period_start,
      period_end: form.period_end,
      version_name: form.version_name.trim() || 'Baseline',
    });
  };

  const inputClass =
    'mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-stocker-primary focus:ring-1 focus:ring-stocker-primary';

  return (
    <div className="border-b border-gray-200 bg-gray-50 px-4 py-4">
      <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-600">
        {t('consensus.newPlanningCycleFormTitle')}
      </h3>
      <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
        <label className="flex flex-col text-xs text-gray-600 md:col-span-2">
          {t('consensus.cycleName')}
          <input
            className={inputClass}
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          />
        </label>
        <label className="flex flex-col text-xs text-gray-600">
          {t('consensus.cycleType')}
          <select
            className={inputClass}
            value={form.cycle_type}
            onChange={(e) => setForm((f) => ({ ...f, cycle_type: e.target.value }))}
          >
            <option value="weekly">{t('consensus.cycleTypes.weekly')}</option>
            <option value="monthly">{t('consensus.cycleTypes.monthly')}</option>
            <option value="quarterly">{t('consensus.cycleTypes.quarterly')}</option>
          </select>
        </label>
        <label className="flex flex-col text-xs text-gray-600">
          {t('consensus.initialPlanVersion')}
          <input
            className={inputClass}
            value={form.version_name}
            onChange={(e) => setForm((f) => ({ ...f, version_name: e.target.value }))}
          />
        </label>
        <label className="flex flex-col text-xs text-gray-600">
          {t('consensus.periodStart')}
          <input
            type="date"
            className={inputClass}
            value={form.period_start}
            onChange={(e) => setForm((f) => ({ ...f, period_start: e.target.value }))}
          />
        </label>
        <label className="flex flex-col text-xs text-gray-600">
          {t('consensus.periodEnd')}
          <input
            type="date"
            className={inputClass}
            value={form.period_end}
            onChange={(e) => setForm((f) => ({ ...f, period_end: e.target.value }))}
          />
        </label>
      </div>
      <p className="mt-2 text-xs text-gray-500">{t('consensus.periodForecastHorizonHint')}</p>
      <div className="mt-4 flex gap-2">
        <button
          type="button"
          className="rounded-md bg-stocker-primary px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
          disabled={saving || !form.name.trim()}
          onClick={() => void submit()}
        >
          {t('consensus.createPlanningCycle')}
        </button>
        <button
          type="button"
          className="rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
          onClick={onCancel}
        >
          {t('common.cancel')}
        </button>
      </div>
    </div>
  );
}
